import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
  forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { NgIconComponent } from "@ng-icons/core";
import { escapeHtml, inlineMarkdown } from "src/app/shared/utils/markdown";

interface SlashCommand {
  id: string;
  label: string;
  description: string;
  icon: string;
  html: string;
}

/**
 * EVA Markdown Editor - Éditeur rich-text avec commandes slash
 *
 * Éditeur WYSIWYG avec toolbar flottante, commandes slash (/) et support markdown.
 * Compatible ControlValueAccessor (ngModel / formControl).
 *
 * @example
 * // Usage simple
 * <eva-markdown-editor [(value)]="content" placeholder="Commencez à écrire..."></eva-markdown-editor>
 *
 * // Avec formControl
 * <eva-markdown-editor [formControl]="descriptionControl"></eva-markdown-editor>
 *
 * // Avec compteur de caractères
 * <eva-markdown-editor [(value)]="notes" [maxlength]="2000" [showCount]="true"></eva-markdown-editor>
 */
@Component({
  selector: "eva-markdown-editor",
  templateUrl: "./eva-markdown-editor.component.html",
  styleUrls: ["./eva-markdown-editor.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, NgIconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EvaMarkdownEditorComponent),
      multi: true,
    },
  ],
})
export class EvaMarkdownEditorComponent implements ControlValueAccessor, AfterViewInit {
  /** Placeholder affiché quand le contenu est vide */
  @Input() placeholder: string = "Commencez à écrire...";
  /** Désactive l'éditeur */
  @Input() disabled: boolean = false;
  /** Longueur maximale du contenu (en caractères) */
  @Input() maxlength: number | null = null;
  /** Nombre de lignes visibles minimum */
  @Input() rows: number = 8;
  /** Affiche le compteur de caractères */
  @Input() showCount: boolean = false;

  /** Émis quand la valeur change — pour le two-way binding [(value)] */
  @Output() valueChange = new EventEmitter<string>();
  /** Émis quand l'éditeur reçoit le focus */
  @Output() onFocus = new EventEmitter<FocusEvent>();
  /** Émis quand l'éditeur perd le focus */
  @Output() onBlur = new EventEmitter<FocusEvent>();

  @ViewChild("editorContent") editorContent!: ElementRef<HTMLDivElement>;

  _value: string = "";
  isFocused: boolean = false;

  // Floating toolbar state
  showToolbar: boolean = false;
  toolbarPosition: { top: number; left: number } = { top: 0, left: 0 };

  // Slash menu state
  showSlashMenu: boolean = false;
  slashMenuPosition: { top: number; left: number } = { top: 0, left: 0 };
  slashFilter: string = "";
  selectedSlashIndex: number = 0;

  private pendingValue: string | null = null;
  private isInternalUpdate: boolean = false;
  private slashStartNode: Node | null = null;
  private slashStartOffset: number = -1;
  private readonly checklistClass = "eva-markdown-editor__checklist";
  private readonly checklistItemClass = "eva-markdown-editor__checklist-item";
  private readonly checklistLabelClass = "eva-markdown-editor__checkbox";
  private readonly checklistTextClass = "eva-markdown-editor__checkbox-text";

  slashCommands: SlashCommand[] = [
    {
      id: "titre",
      label: "Titre",
      description: "Titre principal",
      icon: "heroHashtag",
      html: "h2",
    },
    {
      id: "sous-titre",
      label: "Sous-titre",
      description: "Titre secondaire",
      icon: "heroHashtag",
      html: "h3",
    },
    {
      id: "liste",
      label: "Liste",
      description: "Liste \u00e0 puces",
      icon: "heroListBullet",
      html: "ul",
    },
    {
      id: "numerotee",
      label: "Num\u00e9rot\u00e9e",
      description: "Liste num\u00e9rot\u00e9e",
      icon: "heroBarsArrowUp",
      html: "ol",
    },
    {
      id: "tache",
      label: "Tâche",
      description: "Case à cocher",
      icon: "evaCheckSquare",
      html: "checkbox",
    },
    {
      id: "todo",
      label: "Todo",
      description: "Liste de tâches",
      icon: "evaCheckSquare",
      html: "checkbox",
    },
    {
      id: "checkbox",
      label: "Checkbox",
      description: "Case à cocher",
      icon: "evaCheckSquare",
      html: "checkbox",
    },
    {
      id: "code",
      label: "Code",
      description: "Bloc de code",
      icon: "heroCodeBracket",
      html: "pre",
    },
    {
      id: "separateur",
      label: "S\u00e9parateur",
      description: "Ligne horizontale",
      icon: "heroMinus",
      html: "hr",
    },
    {
      id: "citation",
      label: "Citation",
      description: "Bloc de citation",
      icon: "heroChatBubbleLeft",
      html: "blockquote",
    },
  ];

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
  ) {}

  ngAfterViewInit(): void {
    if (this.pendingValue !== null && this.editorContent?.nativeElement) {
      this.isInternalUpdate = true;
      this.editorContent.nativeElement.innerHTML = this.markdownToHtml(this.pendingValue);
      this.pendingValue = null;
      this.isInternalUpdate = false;
    }
  }

  // ---------------------------------------------------------------------------
  // ControlValueAccessor
  // ---------------------------------------------------------------------------

  @Input()
  get value(): string {
    return this._value;
  }
  set value(val: string) {
    if (val !== this._value) {
      this._value = val;
      this.onChange(val);
      this.valueChange.emit(val);
      if (this.editorContent?.nativeElement && !this.isInternalUpdate) {
        this.isInternalUpdate = true;
        const saved = this.saveSelection();
        this.editorContent.nativeElement.innerHTML = this.markdownToHtml(val);
        this.restoreSelection(saved);
        this.isInternalUpdate = false;
      } else if (!this.editorContent?.nativeElement) {
        // ViewChild not yet available (before ngAfterViewInit) — store for later
        this.pendingValue = val;
      }
    }
  }

  writeValue(value: string): void {
    this._value = value || "";
    if (this.editorContent?.nativeElement) {
      this.isInternalUpdate = true;
      this.editorContent.nativeElement.innerHTML = this.markdownToHtml(this._value);
      this.isInternalUpdate = false;
    } else {
      this.pendingValue = value || "";
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // Character count
  // ---------------------------------------------------------------------------

  get characterCount(): string {
    const current = this._value?.length || 0;
    if (this.maxlength) return `${current} / ${this.maxlength}`;
    return `${current}`;
  }

  // ---------------------------------------------------------------------------
  // Filtered slash commands
  // ---------------------------------------------------------------------------

  get filteredSlashCommands(): SlashCommand[] {
    if (!this.slashFilter) return this.slashCommands;
    const filter = this.slashFilter.toLowerCase();
    return this.slashCommands.filter(
      (cmd) => cmd.label.toLowerCase().includes(filter) || cmd.description.toLowerCase().includes(filter),
    );
  }

  // ---------------------------------------------------------------------------
  // Computed min-height from rows
  // ---------------------------------------------------------------------------

  get minHeight(): number {
    // Approximate line height * rows + padding
    return this.rows * 22 + 24;
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  onInput(event: Event): void {
    if (this.isInternalUpdate) return;

    const editor = event.target as HTMLDivElement;
    const html = editor.innerHTML;

    // Convert HTML to markdown for the model value
    this.isInternalUpdate = true;
    this._value = this.htmlToMarkdown(html);
    this.onChange(this._value);
    this.valueChange.emit(this._value);
    this.isInternalUpdate = false;

    // Update slash filter if menu is open
    if (this.showSlashMenu && this.slashStartNode) {
      this.updateSlashFilter();
    }

    this.cdr.markForCheck();
  }

  onKeydown(event: KeyboardEvent): void {
    // --- Slash menu keyboard navigation ---
    if (this.showSlashMenu) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        const max = this.filteredSlashCommands.length;
        if (max > 0) {
          this.selectedSlashIndex = (this.selectedSlashIndex + 1) % max;
        }
        this.cdr.markForCheck();
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        const max = this.filteredSlashCommands.length;
        if (max > 0) {
          this.selectedSlashIndex = (this.selectedSlashIndex - 1 + max) % max;
        }
        this.cdr.markForCheck();
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        const cmds = this.filteredSlashCommands;
        if (cmds.length > 0 && this.selectedSlashIndex < cmds.length) {
          this.applySlashCommand(cmds[this.selectedSlashIndex]);
        }
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        this.closeSlashMenu();
        this.cdr.markForCheck();
        return;
      }
    }

    // --- Enter dans une checklist : continuer la liste ou sortir si vide ---
    if (event.key === "Enter" && !event.shiftKey) {
      const handled = this.handleChecklistEnter();
      if (handled) {
        event.preventDefault();
        this.syncValueFromEditor();
        this.cdr.markForCheck();
        return;
      }
    }

    // --- Escape closes toolbar ---
    if (event.key === "Escape" && this.showToolbar) {
      this.showToolbar = false;
      this.cdr.markForCheck();
      return;
    }

    // --- Keyboard shortcuts ---
    if ((event.ctrlKey || event.metaKey) && event.key === "b") {
      event.preventDefault();
      this.applyBold();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key === "i") {
      event.preventDefault();
      this.applyItalic();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.key === "k") {
      event.preventDefault();
      this.applyLink();
      return;
    }
    if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === "C") {
      event.preventDefault();
      this.applyCode();
      return;
    }

    // --- Tab inserts 2 spaces ---
    if (event.key === "Tab") {
      event.preventDefault();
      document.execCommand("insertText", false, "  ");
      return;
    }

    // --- Slash detection ---
    if (event.key === "/" && !this.showSlashMenu) {
      // Check if at start of line or after whitespace
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const textBefore = this.getTextBeforeCursor(range);
        // Only trigger at start of a line (empty or whitespace only before cursor)
        if (textBefore.trim() === "") {
          setTimeout(() => {
            const sel = window.getSelection();
            if (sel && sel.rangeCount > 0) {
              const r = sel.getRangeAt(0);
              this.slashStartNode = r.startContainer;
              this.slashStartOffset = r.startOffset - 1; // -1 because "/" was already typed
              if (this.slashStartOffset < 0) this.slashStartOffset = 0;
              this.slashFilter = "";
              this.selectedSlashIndex = 0;
              this.showSlashMenu = true;
              this.updateSlashMenuPosition();
              this.cdr.markForCheck();
            }
          }, 0);
        }
      }
    }
  }

  onMouseUp(): void {
    if (this.showSlashMenu) return;
    this.updateToolbarVisibility();
  }

  onKeyUp(event: KeyboardEvent): void {
    if (this.showSlashMenu) return;

    if (
      event.key === "Shift" ||
      (event.shiftKey && ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key))
    ) {
      this.updateToolbarVisibility();
    }
  }

  handleFocus(event: FocusEvent): void {
    this.isFocused = true;
    this.onFocus.emit(event);
    this.cdr.markForCheck();
  }

  handleBlur(event: FocusEvent): void {
    this.isFocused = false;
    this.onTouched();
    this.onBlur.emit(event);

    setTimeout(() => {
      if (!this.elementRef.nativeElement.contains(document.activeElement)) {
        this.showToolbar = false;
        this.closeSlashMenu();
        this.cdr.markForCheck();
      }
    }, 200);
  }

  onEditorMousedown(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const checkbox = this.findNearestCheckbox(target);
    if (checkbox) {
      // On mousedown, le browser n'a PAS encore toggle le checkbox.
      // preventDefault() empêche le toggle natif dans contenteditable.
      event.preventDefault();
      checkbox.checked = !checkbox.checked;
      this.syncValueFromEditor();
      const textContainer = this.getChecklistTextContainerFromCheckbox(checkbox);
      if (textContainer) {
        this.placeCursorAtEndOfElement(textContainer);
      }
      this.cdr.markForCheck();
      return;
    }

    // Clic dans la zone d'un item checklist (hors texte) -> replacer le curseur dans la zone éditable
    const checklistLabel = this.findNearestChecklistLabel(target);
    if (checklistLabel && !this.isInsideChecklistTextContainer(target, checklistLabel)) {
      event.preventDefault();
      const textContainer = this.getChecklistTextContainer(checklistLabel);
      if (textContainer) {
        this.placeCursorAtEndOfElement(textContainer);
        this.cdr.markForCheck();
      }
    }
  }

  /** Empêche le browser de retoggler le checkbox sur click (après notre mousedown) */
  onEditorClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.findNearestCheckbox(target)) {
      event.preventDefault();
      return;
    }

    // Fix: clicking in empty area of checklist li → place cursor at end of text
    this.correctChecklistCursorPosition();
  }

  private findNearestCheckbox(target: HTMLElement): HTMLInputElement | null {
    // Seul un clic direct sur la checkbox toggle son état
    if (target instanceof HTMLInputElement && target.type === "checkbox") {
      return target;
    }
    return null;
  }

  private findNearestChecklistLabel(target: HTMLElement): HTMLLabelElement | null {
    const nearest = target.closest(`label.${this.checklistLabelClass}`);
    if (nearest instanceof HTMLLabelElement) {
      return nearest;
    }
    return null;
  }

  private getChecklistTextContainer(host: Element | null): HTMLElement | null {
    if (!host) return null;
    const container = host.querySelector(`.${this.checklistTextClass}`);
    if (container instanceof HTMLElement) {
      return container;
    }
    return null;
  }

  private getChecklistTextContainerFromCheckbox(checkbox: HTMLInputElement): HTMLElement | null {
    const label = checkbox.closest(`label.${this.checklistLabelClass}`);
    return this.getChecklistTextContainer(label);
  }

  private isInsideChecklistTextContainer(target: HTMLElement, label: HTMLLabelElement): boolean {
    const textContainer = this.getChecklistTextContainer(label);
    return !!textContainer && textContainer.contains(target);
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const host = this.elementRef.nativeElement;
    if (!host.contains(target)) {
      this.showToolbar = false;
      this.closeSlashMenu();
      this.cdr.markForCheck();
    }
  }

  // ---------------------------------------------------------------------------
  // Toolbar actions
  // ---------------------------------------------------------------------------

  applyBold(): void {
    document.execCommand("bold", false);
    this.editorContent.nativeElement.focus();
    this.syncValueFromEditor();
    this.showToolbar = false;
    this.cdr.markForCheck();
  }

  applyItalic(): void {
    document.execCommand("italic", false);
    this.editorContent.nativeElement.focus();
    this.syncValueFromEditor();
    this.showToolbar = false;
    this.cdr.markForCheck();
  }

  applyHeading(): void {
    document.execCommand("formatBlock", false, "h2");
    this.editorContent.nativeElement.focus();
    this.syncValueFromEditor();
    this.showToolbar = false;
    this.cdr.markForCheck();
  }

  applyList(): void {
    document.execCommand("insertUnorderedList", false);
    this.editorContent.nativeElement.focus();
    this.syncValueFromEditor();
    this.showToolbar = false;
    this.cdr.markForCheck();
  }

  applyCode(): void {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const selectedText = range.toString();
      if (selectedText) {
        const code = document.createElement("code");
        code.textContent = selectedText;
        range.deleteContents();
        range.insertNode(code);
        // Move cursor after code
        range.setStartAfter(code);
        range.setEndAfter(code);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
    this.editorContent.nativeElement.focus();
    this.syncValueFromEditor();
    this.showToolbar = false;
    this.cdr.markForCheck();
  }

  applyLink(): void {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const selectedText = range.toString() || "texte";
      const url = "url";

      const link = document.createElement("a");
      link.href = url;
      link.textContent = selectedText;
      link.target = "_blank";

      range.deleteContents();
      range.insertNode(link);

      // Select the "url" in href so user can type the real URL
      range.setStartAfter(link);
      range.setEndAfter(link);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    this.editorContent.nativeElement.focus();
    this.syncValueFromEditor();
    this.showToolbar = false;
    this.cdr.markForCheck();
  }

  applyCheckbox(): void {
    const ul = document.createElement("ul");
    ul.className = this.checklistClass;
    const li = this.createChecklistItem();
    ul.appendChild(li);

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(ul);
      const textContainer = this.getChecklistTextContainer(li);
      if (textContainer) {
        this.placeCursorAtEndOfElement(textContainer);
      }
    }
    this.editorContent.nativeElement.focus();
    this.syncValueFromEditor();
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // Slash commands
  // ---------------------------------------------------------------------------

  applySlashCommand(command: SlashCommand): void {
    const editor = this.editorContent.nativeElement;

    // Remove the "/" and filter text
    this.removeSlashText();

    // Insert the appropriate HTML element
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      this.closeSlashMenu();
      return;
    }

    const range = selection.getRangeAt(0);

    switch (command.html) {
      case "h2": {
        const h2 = document.createElement("h2");
        h2.innerHTML = "<br>";
        range.insertNode(h2);
        this.placeCursorInside(h2);
        break;
      }
      case "h3": {
        const h3 = document.createElement("h3");
        h3.innerHTML = "<br>";
        range.insertNode(h3);
        this.placeCursorInside(h3);
        break;
      }
      case "ul": {
        const ul = document.createElement("ul");
        const li = document.createElement("li");
        li.innerHTML = "<br>";
        ul.appendChild(li);
        range.insertNode(ul);
        this.placeCursorInside(li);
        break;
      }
      case "ol": {
        const ol = document.createElement("ol");
        const li = document.createElement("li");
        li.innerHTML = "<br>";
        ol.appendChild(li);
        range.insertNode(ol);
        this.placeCursorInside(li);
        break;
      }
      case "checkbox": {
        const ul = document.createElement("ul");
        ul.className = this.checklistClass;
        const li = this.createChecklistItem();
        ul.appendChild(li);
        range.insertNode(ul);
        const textContainer = this.getChecklistTextContainer(li);
        if (textContainer) {
          this.placeCursorAtEndOfElement(textContainer);
        }
        break;
      }
      case "pre": {
        const pre = document.createElement("pre");
        const code = document.createElement("code");
        code.innerHTML = "<br>";
        pre.appendChild(code);
        range.insertNode(pre);
        this.placeCursorInside(code);
        break;
      }
      case "hr": {
        const hr = document.createElement("hr");
        const p = document.createElement("p");
        p.innerHTML = "<br>";
        range.insertNode(p);
        range.insertNode(hr);
        this.placeCursorInside(p);
        break;
      }
      case "blockquote": {
        const bq = document.createElement("blockquote");
        bq.innerHTML = "<br>";
        range.insertNode(bq);
        this.placeCursorInside(bq);
        break;
      }
    }

    this.closeSlashMenu();
    this.syncValueFromEditor();
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------------
  // Toolbar visibility & position
  // ---------------------------------------------------------------------------

  updateToolbarVisibility(): void {
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) {
      this.updateToolbarPosition();
      this.showToolbar = true;
    } else {
      this.showToolbar = false;
    }
    this.cdr.markForCheck();
  }

  updateToolbarPosition(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const editorRect = this.elementRef.nativeElement.getBoundingClientRect();

    this.toolbarPosition = {
      top: rect.top - editorRect.top - 44,
      left: Math.max(0, rect.left - editorRect.left + rect.width / 2 - 100),
    };
  }

  updateSlashMenuPosition(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const editorRect = this.elementRef.nativeElement.getBoundingClientRect();

    this.slashMenuPosition = {
      top: rect.bottom - editorRect.top + 4,
      left: Math.max(0, rect.left - editorRect.left),
    };
  }

  // ---------------------------------------------------------------------------
  // Format state detection (for toolbar active state)
  // ---------------------------------------------------------------------------

  isFormatActive(command: string): boolean {
    try {
      return document.queryCommandState(command);
    } catch {
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  // Markdown <-> HTML conversion
  // ---------------------------------------------------------------------------

  markdownToHtml(md: string): string {
    if (!md) return "";

    const lines = md.split("\n");
    const result: string[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Code block (```)
      if (line.trimStart().startsWith("```")) {
        const codeLines: string[] = [];
        i++;
        while (i < lines.length && !lines[i].trimStart().startsWith("```")) {
          codeLines.push(escapeHtml(lines[i]));
          i++;
        }
        i++; // Skip closing ```
        result.push(`<pre><code>${codeLines.join("\n") || "<br>"}</code></pre>`);
        continue;
      }

      // Horizontal rule
      if (line.trim() === "---" || line.trim() === "***" || line.trim() === "___") {
        result.push("<hr>");
        i++;
        continue;
      }

      // Headings
      if (line.startsWith("### ")) {
        result.push(`<h3>${inlineMarkdown(line.substring(4))}</h3>`);
        i++;
        continue;
      }
      if (line.startsWith("## ")) {
        result.push(`<h2>${inlineMarkdown(line.substring(3))}</h2>`);
        i++;
        continue;
      }

      // Blockquote
      if (line.startsWith("> ")) {
        const quoteLines: string[] = [];
        while (i < lines.length && lines[i].startsWith("> ")) {
          quoteLines.push(inlineMarkdown(lines[i].substring(2)));
          i++;
        }
        result.push(`<blockquote>${quoteLines.join("<br>")}</blockquote>`);
        continue;
      }

      // Checkbox list (- [ ] or - [x])
      if (/^\s*- \[([ xX])\] /.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^\s*- \[([ xX])\] /.test(lines[i])) {
          const match = lines[i].match(/^\s*- \[([ xX])\] (.*)$/);
          if (match) {
            const checked = /x/i.test(match[1]) ? " checked" : "";
            const itemContent = inlineMarkdown(match[2]).trim() || "&nbsp;";
            items.push(
              `<li class="${this.checklistItemClass}"><label class="${this.checklistLabelClass}"><input type="checkbox"${checked}><span class="${this.checklistTextClass}">${itemContent}</span></label></li>`,
            );
          }
          i++;
        }
        result.push(`<ul class="${this.checklistClass}">${items.join("")}</ul>`);
        continue;
      }

      // Unordered list
      if (/^\s*[-*+] /.test(line) && !/^\s*- \[/.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^\s*[-*+] /.test(lines[i]) && !/^\s*- \[/.test(lines[i])) {
          const content = lines[i].replace(/^\s*[-*+] /, "");
          items.push(`<li>${inlineMarkdown(content)}</li>`);
          i++;
        }
        result.push(`<ul>${items.join("")}</ul>`);
        continue;
      }

      // Ordered list
      if (/^\s*\d+\. /.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^\s*\d+\. /.test(lines[i])) {
          const content = lines[i].replace(/^\s*\d+\. /, "");
          items.push(`<li>${inlineMarkdown(content)}</li>`);
          i++;
        }
        result.push(`<ol>${items.join("")}</ol>`);
        continue;
      }

      // Empty line(s)
      if (line.trim() === "") {
        // Compter les lignes vides consécutives
        let emptyCount = 0;
        while (i < lines.length && lines[i].trim() === "") {
          emptyCount++;
          i++;
        }
        // Une seule ligne vide = séparateur de paragraphe (pas d'espace visuel)
        // 2+ lignes vides = espacement intentionnel → ajouter des <p><br></p>
        if (result.length > 0) {
          for (let j = 1; j < emptyCount; j++) {
            result.push("<p><br></p>");
          }
        }
        continue;
      }

      // Regular paragraph
      result.push(`<p>${inlineMarkdown(line)}</p>`);
      i++;
    }

    return result.join("");
  }

  htmlToMarkdown(html: string): string {
    if (!html || html === "<br>" || html === "<p><br></p>") return "";

    const temp = document.createElement("div");
    temp.innerHTML = html;

    return this.nodeToMarkdown(temp).trim();
  }

  // ---------------------------------------------------------------------------
  // Private: HTML node -> markdown conversion
  // ---------------------------------------------------------------------------

  private nodeToMarkdown(node: Node): string {
    const parts: string[] = [];

    for (let i = 0; i < node.childNodes.length; i++) {
      const child = node.childNodes[i];

      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent || "";
        parts.push(text);
        continue;
      }

      if (child.nodeType !== Node.ELEMENT_NODE) continue;

      const el = child as HTMLElement;
      const tag = el.tagName.toLowerCase();

      switch (tag) {
        case "h2":
          parts.push(`## ${this.getInlineMarkdown(el)}\n\n`);
          break;
        case "h3":
          parts.push(`### ${this.getInlineMarkdown(el)}\n\n`);
          break;
        case "p":
        case "div": {
          const content = this.getInlineMarkdown(el);
          if (content === "" || content === "\n") {
            parts.push("\n");
          } else {
            parts.push(`${content}\n\n`);
          }
          break;
        }
        case "br":
          parts.push("\n");
          break;
        case "ul": {
          const isChecklist = el.classList.contains(this.checklistClass);
          for (let j = 0; j < el.children.length; j++) {
            const li = el.children[j] as HTMLElement;
            const checkbox = li.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
            if (checkbox || isChecklist) {
              const checked = checkbox && checkbox.checked ? "x" : " ";
              const text = this.getCheckboxText(li);
              parts.push(`- [${checked}] ${text}\n`);
            } else {
              parts.push(`- ${this.getInlineMarkdown(li)}\n`);
            }
          }
          parts.push("\n");
          break;
        }
        case "ol": {
          for (let j = 0; j < el.children.length; j++) {
            const li = el.children[j] as HTMLElement;
            parts.push(`${j + 1}. ${this.getInlineMarkdown(li)}\n`);
          }
          parts.push("\n");
          break;
        }
        case "blockquote": {
          const lines = this.getInlineMarkdown(el).split("\n");
          for (const line of lines) {
            parts.push(`> ${line}\n`);
          }
          parts.push("\n");
          break;
        }
        case "pre": {
          const codeEl = el.querySelector("code");
          const codeText = codeEl ? codeEl.textContent || "" : el.textContent || "";
          parts.push("```\n" + codeText + "\n```\n\n");
          break;
        }
        case "hr":
          parts.push("---\n\n");
          break;
        case "strong":
        case "b":
          parts.push(`**${this.getInlineMarkdown(el)}**`);
          break;
        case "em":
        case "i": {
          // Don't convert <i class="pi ..."> (PrimeNG icons) to italic
          if (el.classList.contains("pi")) {
            break;
          }
          parts.push(`*${this.getInlineMarkdown(el)}*`);
          break;
        }
        case "code":
          parts.push("`" + (el.textContent || "") + "`");
          break;
        case "a": {
          const href = el.getAttribute("href") || "";
          const text = el.textContent || "";
          parts.push(`[${text}](${href})`);
          break;
        }
        case "label": {
          // Checkbox label
          const cb = el.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
          if (cb) {
            const checked = cb.checked ? "x" : " ";
            const text = this.getCheckboxText(el);
            parts.push(`- [${checked}] ${text}`);
          } else {
            parts.push(el.textContent || "");
          }
          break;
        }
        case "input": {
          // Standalone checkbox (shouldn't happen normally, but handle it)
          const inp = el as HTMLInputElement;
          if (inp.type === "checkbox") {
            const checked = inp.checked ? "x" : " ";
            parts.push(`- [${checked}] `);
          }
          break;
        }
        default:
          // For any other element, recurse
          parts.push(this.nodeToMarkdown(el));
          break;
      }
    }

    return parts.join("");
  }

  private getInlineMarkdown(el: HTMLElement): string {
    const parts: string[] = [];

    for (let i = 0; i < el.childNodes.length; i++) {
      const child = el.childNodes[i];

      if (child.nodeType === Node.TEXT_NODE) {
        parts.push(child.textContent || "");
        continue;
      }

      if (child.nodeType !== Node.ELEMENT_NODE) continue;

      const childEl = child as HTMLElement;
      const tag = childEl.tagName.toLowerCase();

      switch (tag) {
        case "strong":
        case "b":
          parts.push(`**${this.getInlineMarkdown(childEl)}**`);
          break;
        case "em":
        case "i":
          if (!childEl.classList.contains("pi")) {
            parts.push(`*${this.getInlineMarkdown(childEl)}*`);
          }
          break;
        case "code":
          parts.push("`" + (childEl.textContent || "") + "`");
          break;
        case "a": {
          const href = childEl.getAttribute("href") || "";
          const text = childEl.textContent || "";
          parts.push(`[${text}](${href})`);
          break;
        }
        case "br":
          parts.push("\n");
          break;
        case "input": {
          // skip checkboxes in inline context (handled by parent label)
          break;
        }
        case "label": {
          const cb = childEl.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
          if (cb) {
            // Skip — handled by the list context
          } else {
            parts.push(childEl.textContent || "");
          }
          break;
        }
        default:
          parts.push(this.getInlineMarkdown(childEl));
          break;
      }
    }

    return parts.join("");
  }

  private getCheckboxText(el: HTMLElement): string {
    let text = "";
    const walk = (node: Node): void => {
      for (let i = 0; i < node.childNodes.length; i++) {
        const child = node.childNodes[i];
        if (child.nodeType === Node.TEXT_NODE) {
          text += child.textContent || "";
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          const childEl = child as HTMLElement;
          if (childEl.tagName === "INPUT") continue; // Skip checkbox input
          if (childEl.tagName === "STRONG" || childEl.tagName === "B") {
            text += `**${childEl.textContent || ""}**`;
          } else if (childEl.tagName === "EM" || childEl.tagName === "I") {
            if (!childEl.classList.contains("pi")) {
              text += `*${childEl.textContent || ""}*`;
            }
          } else if (childEl.tagName === "CODE") {
            text += "`" + (childEl.textContent || "") + "`";
          } else {
            walk(childEl); // Recurse into labels, spans, etc.
          }
        }
      }
    };
    walk(el);
    return text.trim();
  }

  // ---------------------------------------------------------------------------
  // Private: contenteditable helpers
  // ---------------------------------------------------------------------------

  /**
   * Gère Enter dans une checklist (comportement Notion) :
   * - Si le li courant a du texte → crée un nouveau li avec checkbox vide après
   * - Si le li courant est vide → supprime le li et sort de la liste (insère un <p>)
   * @returns true si l'événement a été géré
   */
  private handleChecklistEnter(): boolean {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return false;

    const range = selection.getRangeAt(0);

    // Trouver le <li> parent
    let node: Node | null = range.startContainer;
    let li: HTMLElement | null = null;
    while (node && node !== this.editorContent.nativeElement) {
      if (node instanceof HTMLElement && node.tagName === "LI") {
        li = node;
        break;
      }
      node = node.parentNode;
    }
    if (!li) return false;

    // Vérifier que c'est dans une checklist (ul avec checkbox)
    const parentUl = li.closest("ul");
    if (!parentUl) return false;
    const hasCheckbox = li.querySelector('input[type="checkbox"]');
    if (!hasCheckbox) return false;

    // Extraire le texte du li (sans le checkbox)
    const text = this.getCheckboxText(li).trim();

    if (text === "") {
      // Li vide → sortir de la liste : supprimer le li, insérer un <p> après la <ul>
      li.remove();

      // Si la liste est maintenant vide, la supprimer aussi
      if (parentUl.children.length === 0) {
        const p = document.createElement("p");
        p.innerHTML = "<br>";
        parentUl.parentNode?.replaceChild(p, parentUl);
        this.placeCursorInside(p);
      } else {
        // Insérer un <p> après la liste
        const p = document.createElement("p");
        p.innerHTML = "<br>";
        parentUl.after(p);
        this.placeCursorInside(p);
      }
      return true;
    }

    // Li avec texte → créer un nouveau li avec checkbox vide après le li courant
    const newLi = this.createChecklistItem();

    // Insérer après le li courant
    li.after(newLi);

    // Placer le curseur dans la zone texte de la nouvelle checkbox
    const textContainer = this.getChecklistTextContainer(newLi);
    if (textContainer) {
      this.placeCursorAtEndOfElement(textContainer);
    }
    return true;
  }

  private syncValueFromEditor(): void {
    if (!this.editorContent?.nativeElement) return;

    // Synchroniser les attributs HTML checked avec les propriétés DOM.
    // innerHTML ne sérialise PAS .checked (propriété), seulement l'attribut [checked].
    // Sans ça, htmlToMarkdown lit un clone où tous les checkboxes sont unchecked.
    this.editorContent.nativeElement.querySelectorAll('input[type="checkbox"]').forEach((cb: Element) => {
      const input = cb as HTMLInputElement;
      if (input.checked) {
        input.setAttribute("checked", "");
      } else {
        input.removeAttribute("checked");
      }
    });

    const html = this.editorContent.nativeElement.innerHTML;
    this.isInternalUpdate = true;
    this._value = this.htmlToMarkdown(html);
    this.onChange(this._value);
    this.valueChange.emit(this._value);
    this.isInternalUpdate = false;
  }

  private saveSelection(): {
    node: Node | null;
    offset: number;
  } | null {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      return { node: range.startContainer, offset: range.startOffset };
    }
    return null;
  }

  private restoreSelection(saved: { node: Node | null; offset: number } | null): void {
    if (!saved || !saved.node) return;
    try {
      const selection = window.getSelection();
      if (selection) {
        const range = document.createRange();
        range.setStart(saved.node, saved.offset);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } catch {
      // Node may no longer exist after innerHTML replacement
    }
  }

  private placeCursorInside(el: HTMLElement): void {
    const selection = window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(true); // Collapse to start
    selection.removeAllRanges();
    selection.addRange(range);
    this.editorContent.nativeElement.focus();
  }

  private placeCursorAtEndOfElement(el: HTMLElement): void {
    const selection = window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    this.editorContent.nativeElement.focus();
  }

  private placeCursorAfter(el: HTMLElement): void {
    const selection = window.getSelection();
    if (!selection) return;
    const range = document.createRange();
    range.setStartAfter(el);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    this.editorContent.nativeElement.focus();
  }

  /** Corrige le curseur quand on clique dans la zone vide d'un li checklist */
  private correctChecklistCursorPosition(): void {
    setTimeout(() => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      let node: Node | null = range.startContainer;

      // Trouver le <li> parent
      let li: HTMLElement | null = null;
      while (node && node !== this.editorContent?.nativeElement) {
        if (node instanceof HTMLElement && node.tagName === "LI") {
          li = node;
          break;
        }
        node = node.parentNode;
      }
      if (!li) return;

      // Vérifier que c'est un li avec checkbox
      const checkbox = li.querySelector('input[type="checkbox"]');
      if (!checkbox) return;

      const textContainer = this.getChecklistTextContainer(li);
      if (!textContainer) return;
      if (!textContainer.contains(range.startContainer)) {
        this.placeCursorAtEndOfElement(textContainer);
      }
    }, 0);
  }

  private createChecklistItem(textHtml: string = "&nbsp;", checked: boolean = false): HTMLLIElement {
    const li = document.createElement("li");
    li.className = this.checklistItemClass;

    const label = document.createElement("label");
    label.className = this.checklistLabelClass;

    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.checked = checked;
    if (checked) {
      checkbox.setAttribute("checked", "");
    }

    const textContainer = document.createElement("span");
    textContainer.className = this.checklistTextClass;
    textContainer.innerHTML = textHtml.trim() ? textHtml : "&nbsp;";

    label.appendChild(checkbox);
    label.appendChild(textContainer);
    li.appendChild(label);

    return li;
  }

  private getTextBeforeCursor(range: Range): string {
    // Get the text content of the current line before the cursor
    const container = range.startContainer;
    if (container.nodeType === Node.TEXT_NODE) {
      return (container.textContent || "").substring(0, range.startOffset);
    }
    return "";
  }

  private updateSlashFilter(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      this.closeSlashMenu();
      return;
    }

    const range = selection.getRangeAt(0);
    const container = range.startContainer;

    if (container.nodeType === Node.TEXT_NODE) {
      const text = container.textContent || "";
      const cursorPos = range.startOffset;

      // Find the "/" in the current text
      const slashIdx = text.lastIndexOf("/", cursorPos - 1);
      if (slashIdx >= 0) {
        this.slashFilter = text.substring(slashIdx + 1, cursorPos);
        this.selectedSlashIndex = 0;

        // If cursor moved before the slash, close menu
        if (cursorPos <= slashIdx) {
          this.closeSlashMenu();
        }
      } else {
        this.closeSlashMenu();
      }
    } else {
      this.closeSlashMenu();
    }
  }

  private removeSlashText(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const container = range.startContainer;

    if (container.nodeType === Node.TEXT_NODE) {
      const text = container.textContent || "";
      const cursorPos = range.startOffset;
      const slashIdx = text.lastIndexOf("/", cursorPos - 1);

      if (slashIdx >= 0) {
        // Remove from "/" to cursor position
        const newText = text.substring(0, slashIdx) + text.substring(cursorPos);
        container.textContent = newText;

        // Reposition cursor
        const newRange = document.createRange();
        newRange.setStart(container, slashIdx);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
      }
    }
  }

  private closeSlashMenu(): void {
    this.showSlashMenu = false;
    this.slashFilter = "";
    this.selectedSlashIndex = 0;
    this.slashStartNode = null;
    this.slashStartOffset = -1;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  focus(): void {
    this.editorContent?.nativeElement?.focus();
  }
}
